import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { AvatarColor, AvatarSize, AvatarTheme } from '../types';
import { IconType } from '@/shared/types/types';

/**
 * ============================================
 * AVATAR CONSTANTS - DESIGN TOKENS
 * ============================================
 * Constantes centralizadas para facilitar la personalización
 * del componente Avatar
 */

/**
 * ============================================
 * DEFAULT VALUES
 * ============================================
 */
export const DEFAULT_ICON: IconType = faUser;
export const DEFAULT_COLOR: AvatarColor = 'blue';
export const DEFAULT_THEME: AvatarTheme = 'solid';
export const DEFAULT_SIZE: AvatarSize = 'md';

/**
 * ============================================
 * COLOR SCHEMES - TEMA SOLID
 * ============================================
 * Colores para el tema solid (fondo sólido + texto blanco)
 *
 * Para personalizar:
 * 1. Modifica los valores de Tailwind
 * 2. O agrega nuevos colores al objeto
 */
export const SOLID_COLORS: Record<AvatarColor, string> = {
  blue: 'ui-bg-fill-all-colors-blue ui-text-on-color',
  red: 'ui-bg-fill-all-colors-red ui-text-on-color',
  orange: 'ui-bg-fill-all-colors-orange ui-text-on-color',
  yellow: 'ui-bg-fill-all-colors-yellow ui-text-on-color',
  lime: 'ui-bg-fill-all-colors-lime ui-text-on-color',
  green: 'ui-bg-fill-all-colors-green ui-text-on-color',
  cyan: 'ui-bg-fill-all-colors-cyan ui-text-on-color',
  azure: 'ui-bg-fill-all-colors-azure ui-text-on-color',
  violet: 'ui-bg-fill-all-colors-violet ui-text-on-color',
  magenta: 'ui-bg-fill-all-colors-magenta ui-text-on-color',
  rose: 'ui-bg-fill-all-colors-rose ui-text-on-color',
};

/**
 * ============================================
 * COLOR SCHEMES - TEMA SOFT
 * ============================================
 * Colores para el tema soft (fondo claro + texto oscuro)
 *
 * Para personalizar:
 * 1. Modifica los valores de Tailwind
 * 2. O agrega nuevos colores al objeto
 */
export const SOFT_COLORS: Record<AvatarColor, string> = {
  blue: 'ui-bg-surface-all-colors-blue ui-text-icon-allColors-blue',
  red: 'ui-bg-surface-all-colors-red ui-text-icon-allColors-red',
  orange: 'ui-bg-surface-all-colors-orange ui-text-icon-allColors-orange',
  yellow: 'ui-bg-surface-all-colors-yellow ui-text-icon-allColors-yellow',
  lime: 'ui-bg-surface-all-colors-lime ui-text-icon-allColors-lime',
  green: 'ui-bg-surface-all-colors-green ui-text-icon-allColors-green',
  cyan: 'ui-bg-surface-all-colors-cyan ui-text-icon-allColors-cyan',
  azure: 'ui-bg-surface-all-colors-azure ui-text-icon-allColors-azure',
  violet: 'ui-bg-surface-all-colors-violet ui-text-icon-allColors-violet',
  magenta: 'ui-bg-surface-all-colors-magenta ui-text-icon-allColors-magenta',
  rose: 'ui-bg-surface-all-colors-rose ui-text-icon-allColors-rose',
};

/**
 * Paleta específica para avatares tipo 'letter' con tema soft.
 * Inicialmente hereda los mismos valores que icon para mantener consistencia,
 * pero queda lista para personalizar tipografía/contraste más adelante.
 */
export const SOFT_LETTER_COLORS: Record<AvatarColor, string> = {
  ...SOFT_COLORS,
  // Marca temporal para resaltar letras en tema soft
  blue: 'ui-bg-surface-all-colors-blue ui-text-icon-allColors-blue',
  red: 'ui-bg-surface-all-colors-red ui-text-icon-allColors-red',
  orange: 'ui-bg-surface-all-colors-orange ui-text-icon-allColors-orange',
  yellow: 'ui-bg-surface-all-colors-yellow ui-text-icon-allColors-yellow',
  lime: 'ui-bg-surface-all-colors-lime ui-text-icon-allColors-lime',
  green: 'ui-bg-surface-all-colors-green ui-text-icon-allColors-green',
  cyan: 'ui-bg-surface-all-colors-cyan ui-text-icon-allColors-cyan',
  azure: 'ui-bg-surface-all-colors-azure ui-text-icon-allColors-azure',
  violet: 'ui-bg-surface-all-colors-violet ui-text-icon-allColors-violet',
  magenta: 'ui-bg-surface-all-colors-magenta ui-text-icon-allColors-magenta',
  rose: 'ui-bg-surface-all-colors-rose ui-text-icon-allColors-rose',
};

/**
 * ============================================
 * SIZE CONFIGURATIONS
 * ============================================
 * Tamaños del avatar (ancho, alto y tamaño de texto)
 *
 * Para personalizar:
 * 1. Modifica los valores de ancho/alto (ej: ui-w-16 ui-h-16)
 * 2. Ajusta el tamaño del texto (ej: ui-text-[6px])
 *
 * Nota: Los tamaños están en múltiplos de 4px (escala Tailwind)
 */
export const AVATAR_SIZES: Record<AvatarSize, string> = {
  xxs: 'ui-w-16 ui-h-16 ui-text-body-xs-regular ui-font-body-xs-regular',
  xs: 'ui-w-24 ui-h-24 ui-text-body-xs-regular ui-font-body-xs-regular',
  sm: 'ui-w-32 ui-h-32 ui-text-body-sm-regular ui-font-body-md-regular',
  md: 'ui-w-40 ui-h-40 ui-text-body-md-regular ui-font-body-md-regular',
  lg: 'ui-w-48 ui-h-48 ui-text-body-lg-regular ui-font-body-lg-regular',
  xl: 'ui-w-64 ui-h-64 ui-text-heading-h2 ui-font-heading-h1',
};

/**
 * ============================================
 * ICON SIZE MAPPING
 * ============================================
 * Mapeo de tamaños de avatar a tamaños de icono
 *
 * Para personalizar:
 * 1. Ajusta el tamaño del icono según el tamaño del avatar
 * 2. Los valores deben corresponder a los tamaños del componente Icon
 */
export const ICON_SIZES: Record<AvatarSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'> = {
  xxs: 'xs',
  xs: 'sm',
  sm: 'md',
  md: 'lg',
  lg: 'xl',
  xl: '2xl',
};

/**
 * ============================================
 * BORDER & SHAPE CONFIGURATIONS
 * ============================================
 */

/** Ancho del borde */
export const STROKE_WIDTH = 'ui-border-1';

/** Color de borde para avatar tipo 'people' */
export const PEOPLE_STROKE_COLOR = 'ui-border';

/** Colores de borde para el tema soft (icon y letter) */
export const SOFT_STROKE_COLORS: Record<AvatarColor, string> = {
  blue: 'ui-border-all-colors-blue',
  red: 'ui-border-all-colors-red',
  orange: 'ui-border-all-colors-orange',
  yellow: 'ui-border-all-colors-yellow',
  lime: 'ui-border-all-colors-lime',
  green: 'ui-border-all-colors-green',
  cyan: 'ui-border-all-colors-cyan',
  azure: 'ui-border-all-colors-azure',
  violet: 'ui-border-all-colors-violet',
  magenta: 'ui-border-all-colors-magenta',
  rose: 'ui-border-all-colors-rose',
};

/** Colores de borde para el tema solid (icon y letter) */
export const SOLID_STROKE_COLORS: Record<AvatarColor, string> = {
  blue: 'ui-border-fill-all-colors-blue',
  red: 'ui-border-fill-all-colors-red',
  orange: 'ui-border-fill-all-colors-orange',
  yellow: 'ui-border-fill-all-colors-yellow',
  lime: 'ui-border-fill-all-colors-lime',
  green: 'ui-border-fill-all-colors-green',
  cyan: 'ui-border-fill-all-colors-cyan',
  azure: 'ui-border-fill-all-colors-azure',
  violet: 'ui-border-fill-all-colors-violet',
  magenta: 'ui-border-fill-all-colors-magenta',
  rose: 'ui-border-fill-all-colors-rose',
};

/** Clase para forma circular (pill=true) */
export const PILL_SHAPE_CLASS = 'ui-rounded-control-pill';

/** Clase por defecto para forma redondeada (pill=false) */
export const ROUNDED_SHAPE_CLASS = 'ui-rounded-[10px]';

/**
 * Ajustes de radio cuando `pill=false` en tamaños pequeños.
 * xxs -> 6px, xs -> 8px, resto -> 10px por defecto.
 */
export const ROUNDED_SHAPE_BY_SIZE: Partial<Record<AvatarSize, string>> = {
  xxs: 'ui-rounded-[6px]',
  xs: 'ui-rounded-[8px]',
};

/**
 * ============================================
 * TEXT CONFIGURATION
 * ============================================
 */

/** Longitud máxima de texto para avatares tipo 'letter' */
export const MAX_TEXT_LENGTH = 3;

/**
 * ============================================
 * DISABLED STATE CONFIGURATION
 * ============================================
 * Estilos para el estado deshabilitado
 *
 * Para personalizar:
 * 1. Modifica las clases de opacidad o background
 * 2. Ajusta el filtro grayscale
 */

/** Clases para el estado disabled */
export const DISABLED_CLASS = 'ui-opacity-50 ui-cursor-not-allowed';

/** Filtro grayscale para disabled */
export const DISABLED_GRAYSCALE_FILTER = 'ui-grayscale';

/** Background para disabled (people avatar) */
export const DISABLED_PEOPLE_BG = 'ui-bg-fill-disabled';

/** Filtro de mezcla para imágenes disabled (people avatar) */
export const DISABLED_PEOPLE_BLEND = 'ui-mix-blend-luminosity';

/** Color de texto para disabled (letter avatar) */
export const DISABLED_TEXT_COLOR = 'ui-text-disabled';

/**
 * ============================================
 * STACK CONFIGURATION
 * ============================================
 * Configuración para avatares apilados (stack)
 * Márgenes negativos y padding según el tamaño
 */

/** Márgenes negativos para cada avatar en el stack */
export const STACK_NEGATIVE_MARGINS: Record<AvatarSize, string> = {
  xxs: 'ui--mr-8',
  xs: 'ui--mr-12',
  sm: 'ui--mr-16',
  md: 'ui--mr-20',
  lg: 'ui--mr-24',
  xl: 'ui--mr-32',
};

/** Padding derecho del contenedor del stack */
export const STACK_PADDING_RIGHT: Record<AvatarSize, string> = {
  xxs: 'ui-pr-8',
  xs: 'ui-pr-12',
  sm: 'ui-pr-16',
  md: 'ui-pr-20',
  lg: 'ui-pr-24',
  xl: 'ui-pr-32',
};
